import { useCallback, useEffect, useState } from 'react';
import { Badge, Field, Note, Panel, Spinner } from '@wyocspay/ui';
import { money } from './format.js';
import {
  ApiError,
  api,
  type PricingAnalysis,
  type SubscriptionOverview,
  type SubscriptionTier,
} from './api.js';

const TIER_LABEL: Record<SubscriptionTier, string> = {
  special: 'Special - no platform fee',
  vipplus: 'VIP+ - no fees at all',
};

/**
 * Fee-waiver packages, from the merchant's side.
 *
 * A merchant can pay a fixed monthly amount instead of a per-payment platform
 * fee. "Special" removes the platform fee (Stripe's own fee stays); "VIP+"
 * removes every fee. The price is set by the platform after looking at this
 * account's own history, so what shows here is an estimate; the real figure is
 * confirmed on approval, and the first month is paid before the package starts.
 */
export function PackagesView() {
  const [data, setData] = useState<SubscriptionOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState<SubscriptionTier | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.subscription());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Spinner label="Loading packages…" />;
  if (error) return <Note tone="danger">{error}</Note>;
  if (!data) return null;

  const openApp = data.applications.find((a) => ['PENDING', 'INFO_REQUESTED'].includes(a.status));
  const { current } = data;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="How packages work">
        Instead of a fee on every payment, you can pay a fixed amount each month. <strong>Special</strong>{' '}
        removes the WyoCSPay platform fee - Stripe's own processing fee still applies. <strong>VIP+</strong>{' '}
        removes every fee. The monthly price is set for your account from your own recent volume, you pay
        the first month up front, and the package starts once that payment clears.
      </Note>

      {current ? <CurrentPackage overview={data} onChanged={load} /> : null}

      {openApp ? (
        <Panel title="Your request">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>{TIER_LABEL[openApp.tier]}</strong>
            <Badge tone={openApp.status === 'INFO_REQUESTED' ? 'warn' : 'pending'}>
              {openApp.status.replace(/_/g, ' ')}
            </Badge>
          </div>
          {openApp.infoRequest ? (
            <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--wc-orange)' }}>{openApp.infoRequest}</p>
          ) : (
            <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--wc-muted)' }}>
              The platform team is reviewing your request. You will see the price here once it is approved.
            </p>
          )}
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="wc-btn wc-btn-sm wc-btn-secondary"
              onClick={async () => {
                await api.cancelSubscriptionApplication(openApp.id);
                await load();
              }}
            >
              Withdraw request
            </button>
          </div>
        </Panel>
      ) : null}

      {!current && !openApp ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <TierCard
            tier="special"
            analysis={data.estimate.special}
            onRequest={() => setRequesting('special')}
          />
          <TierCard
            tier="vipplus"
            analysis={data.estimate.vipplus}
            onRequest={() => setRequesting('vipplus')}
          />
        </div>
      ) : null}

      {requesting ? (
        <RequestForm
          tier={requesting}
          onCancel={() => setRequesting(null)}
          onDone={async () => {
            setRequesting(null);
            await load();
          }}
        />
      ) : null}
    </div>
  );
}

function CurrentPackage({ overview, onChanged }: { overview: SubscriptionOverview; onChanged: () => void }) {
  const sub = overview.current!;
  const tone = sub.status === 'ACTIVE' ? 'ok' : sub.status === 'PAST_DUE' ? 'fail' : 'pending';
  return (
    <Panel title="Your package">
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 15 }}>{TIER_LABEL[sub.tier]}</strong>
        <Badge tone={tone}>{sub.status.replace(/_/g, ' ')}</Badge>
      </div>
      <p style={{ margin: '8px 0 0', fontSize: 14 }}>
        <span className="wc-num">{money(sub.monthlyAmount, sub.currency)}</span> / month
        {sub.currentPeriodEnd ? (
          <span style={{ color: 'var(--wc-muted)' }}> · renews {new Date(sub.currentPeriodEnd).toLocaleDateString()}</span>
        ) : null}
      </p>

      {sub.status === 'PENDING_PAYMENT' && overview.payUrl ? (
        <div style={{ marginTop: 12 }}>
          <Note tone="warn" title="One step left">
            Pay the first month to activate your package.
          </Note>
          <a className="wc-btn" href={overview.payUrl} style={{ textDecoration: 'none', marginTop: 10, display: 'inline-block' }}>
            Pay {money(sub.monthlyAmount, sub.currency)} via terminal
          </a>
        </div>
      ) : null}

      {sub.status === 'PAST_DUE' && overview.payUrl ? (
        <div style={{ marginTop: 12 }}>
          <Note tone="danger" title="Payment overdue">
            Your fee waiver has paused. Pay the outstanding month to restore it.
          </Note>
          <a className="wc-btn" href={overview.payUrl} style={{ textDecoration: 'none', marginTop: 10, display: 'inline-block' }}>
            Pay {money(sub.monthlyAmount, sub.currency)} via terminal
          </a>
        </div>
      ) : null}

      <label style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 16, fontSize: 13.5, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={sub.autoDebit}
          onChange={async (e) => {
            await api.setSubscriptionAutoDebit(e.target.checked);
            onChanged();
          }}
          style={{ width: 16, height: 16, accentColor: 'var(--wc-blue)' }}
        />
        Charge my card automatically each month (otherwise you get a payment link to pay each month)
      </label>
    </Panel>
  );
}

function TierCard({
  tier,
  analysis,
  onRequest,
}: {
  tier: SubscriptionTier;
  analysis: PricingAnalysis;
  onRequest: () => void;
}) {
  return (
    <Panel>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <strong style={{ fontSize: 15 }}>{TIER_LABEL[tier]}</strong>
          <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--wc-muted)' }}>
            {tier === 'special'
              ? 'No WyoCSPay platform fee on your payments. Stripe’s processing fee still applies.'
              : 'No fees at all on your payments - the platform covers Stripe’s processing fee too.'}
          </p>
          <p style={{ margin: '10px 0 0', fontSize: 14 }}>
            Estimated from{' '}
            <span className="wc-num">{money(analysis.floor, analysis.currency)}</span>
            /month
            <span style={{ color: 'var(--wc-faint)' }}> · confirmed by the platform</span>
          </p>
          {tier === 'vipplus' && !analysis.vipplusFullyEffective ? (
            <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--wc-orange)' }}>
              Note: on your current account Stripe’s fee can only be fully removed after a one-time
              re-onboarding. Until then VIP+ removes the platform fee like Special. The team will
              explain when you ask.
            </p>
          ) : null}
        </div>
        <button type="button" className="wc-btn wc-btn-sm" onClick={onRequest}>
          Request
        </button>
      </div>
    </Panel>
  );
}

function RequestForm({
  tier,
  onCancel,
  onDone,
}: {
  tier: SubscriptionTier;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [justification, setJustification] = useState('');
  const [volume, setVolume] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.requestSubscription({ tier, justification: justification || undefined, volume: volume || undefined });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={`Request: ${TIER_LABEL[tier]}`}>
      <Field label="Anything you'd like the team to know? (optional)">
        <textarea
          className="wc-input"
          rows={3}
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          style={{ resize: 'vertical' }}
        />
      </Field>
      <Field label="Expected monthly volume (optional)">
        <input className="wc-input" value={volume} onChange={(e) => setVolume(e.target.value)} placeholder="e.g. $40,000 a month" />
      </Field>

      {error ? <div style={{ marginBottom: 12 }}><Note tone="danger">{error}</Note></div> : null}

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="wc-btn" onClick={submit} disabled={busy}>
          {busy ? 'Sending…' : 'Send request'}
        </button>
        <button type="button" className="wc-btn wc-btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </Panel>
  );
}
